import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames } from '../../codegen';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import { PseudonymAppointmentsStep_8Aliases } from '../../codegen/constants/aliases/pseudonym-appointments-step-8-aliases';
import { Inject } from '@odinjs/odin';
import { DeckAPI, MapperAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { Value } from '@philips-internal/oswald-ui-core';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ScheduleItem } from '../../interfaces/schedules-suggestions';
import DateFormatterUtil from '../../utils/date-utils/date-formatter-util';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { AppointmentsIcons, AppointmentsSteps, ScheduleExpressionType, ScheduleType } from './pseudonym-types';
import moment from 'moment';
import './pseudonym-appointments-step-8-controller.css';
import { MapperGroupeSummary } from './pseudonym-interfaces';
import { TELECONSULTATION_EXPRESSION_TYPE, TELECONSULTATION_TYPE } from './pseudonym-enums';

@Controller({ component: PseudonymAppointmentsStep_8Aliases.LAYOUT_SUMMARY, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_8 })
export default class PseudonymAppointmentsStep8Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_8Aliases.PANEL_SUMMARY_PROCEDURES)
    panelSummaryProceduresApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.PANEL_SUMMARY_CONSULTATION)
    panelSummaryConsultationsApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.PANEL_SUMMARY_EXAMS)
    panelSummaryExamsApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.PANEL_SUMMARY_SERVICE)
    panelSummaryServicesApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.PANEL_SUMMARY_WAITING_LIST)
    panelSummaryWaitingListApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.PANEL_LOADER)
    panelLoaderConfirmationApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.DECK_SUMMARY_CONSULTATION)
    deckSummaryConsultationsApi!: DeckAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.DECK_SUMMARY_EXAM)
    deckSummaryExamApi!: DeckAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.DECK_SUMMARY_SERVICE)
    deckSummaryServiceApi!: DeckAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.DECK_SUMMARY_WAITING_LIST)
    deckSummaryWaitingListApi!: DeckAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.MAPPER_GROUPE_SUMMARY_CONSULTATION)
    mapperSummaryConsultationsApi!: MapperAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.MAPPER_GROUPE_SUMMARY_EXAM)
    mapperSummaryExamApi!: MapperAPI;

  @Inject(PseudonymAppointmentsStep_8Aliases.MAPPER_GROUPE_SUMMARY_WAITING_LIST)
    mapperWaitingListExamApi!: MapperAPI;

  @OnMount({ target: PseudonymAppointmentsStep_8Aliases.LAYOUT_SUMMARY })
  handleMountLayoutSuggestion(): void {
    this.panelSummaryExamsApi.setVisible(false);
    this.panelSummaryServicesApi.setVisible(false);
    this.panelSummaryConsultationsApi.setVisible(false);
    this.panelLoaderConfirmationApi.setVisible(false);

    PseudonymAppointmentModel.stepEight.panelLoaderConfirmationApi = this.panelLoaderConfirmationApi;
    PseudonymAppointmentModel.stepEight.populateSummaryCardsWithSelectedItems = () => this.populateSummaryCardsWithSelectedItems();
  }

  @OnMount({ target: PseudonymAppointmentsStep_8Aliases.MAPPER_GROUPE_SUMMARY_CONSULTATION })
  handleMapperSummaryConsultation(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    const selectedItemsList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;

    const selectedConsultationItems = selectedItemsList.filter(item => item.type === ScheduleType.CONSULTATION && !!item.startDate);
    this.setSelectedSuggestionsIfExists(mapperApi, this.panelSummaryConsultationsApi, selectedConsultationItems);
  }

  @OnMount({ target: PseudonymAppointmentsStep_8Aliases.MAPPER_GROUPE_SUMMARY_EXAM })
  handleMapperSummaryExam(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    const selectedItemsList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;

    const selectedExamItems = selectedItemsList.filter(item => item.type === ScheduleType.EXAM && !!item.startDate);
    this.setSelectedSuggestionsIfExists(mapperApi, this.panelSummaryExamsApi, selectedExamItems);
  }

  @OnMount({ target: PseudonymAppointmentsStep_8Aliases.MAPPER_GROUPE_SUMMARY_SERVICE })
  handleMapperSummaryService(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    const selectedItemsList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;

    const selectedExamItems = selectedItemsList.filter(item => item.type === ScheduleType.SERVICE && !!item.startDate);
    this.setSelectedSuggestionsIfExists(mapperApi, this.panelSummaryServicesApi, selectedExamItems);
  }

  @OnMount({ target: PseudonymAppointmentsStep_8Aliases.MAPPER_GROUPE_SUMMARY_WAITING_LIST })
  handleMapperSummaryWaitingList(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    const selectedItemsList = this.getArrayScheduleItemCopy(PseudonymAppointmentModel.stepSeven.suggestions.suggestionList);

    const selectedWaitingListItems = selectedItemsList.filter(item => {
      if (!item.hasSchedulingTimes || item.isEnabledToScheduleWaitingList) {
        return this.filterSuggestionWaitingList(item.nrSeqAgeintItem);
      }
      return false;
    });

    this.additionalInformationWaitingList(selectedWaitingListItems);
    this.setSelectedSuggestionsIfExists(mapperApi, this.panelSummaryWaitingListApi, selectedWaitingListItems);
  }

  private setSelectedSuggestionsIfExists(mapperApi: MapperAPI, panelApi: PanelAPI, suggestions: Array<ScheduleItem>): void {
    if (!suggestions?.length) {
      panelApi.setVisible(false);
      return;
    }

    mapperApi.setData([]);

    const data: MapperGroupeSummary[] = [];
    suggestions.forEach(item => {
      data.push({
        addressNeighborhood: !item.addressNeighborhood ? '-' : item.addressNeighborhood,
        addressStreet: !item.addressStreet ? '-' : item.addressStreet,
        appointmentType: item.dsProcInterno ? item.dsProcInterno : this.getExpressionType(item.appointmentType),
        doctorName: item.subtitle1 ? item.subtitle1 : '-',
        dsEstablishment: item.dsEstablishment,
        image: item.image ? item.image : this.getProcedureIcon(item.appointmentType),
        price: item.isShowPrice ? item.price : '',
        scheduleDay: DateFormatterUtil.shortDate(item.startDate),
        scheduleTime: moment(item.startDate).format('HH:mm'),
        teleconsultationType: this.getTeleconsultationTypeExpression(item, item.isWaitingListItem)
      });
    });

    mapperApi.setData(data as unknown as Record<string, Value>[]);
    this.setWizardStepDone(AppointmentsSteps.STEP_8_SUGGESTION_SUMMARY, true);
  }

  private populateSummaryCardsWithSelectedItems(): void {
    this.panelSummaryExamsApi.setVisible(false);
    this.panelSummaryServicesApi.setVisible(false);
    this.panelSummaryConsultationsApi.setVisible(false);
    this.panelSummaryWaitingListApi.setVisible(false);
    this.panelLoaderConfirmationApi.setVisible(true);

    PseudonymAppointmentModel.stepEight.hasWaitingList = !!PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;
    PseudonymAppointmentModel.stepEight.hasSelectedExams = this.filterSuggestionByType(ScheduleType.EXAM);
    PseudonymAppointmentModel.stepEight.hasSelectedSevices = this.filterSuggestionByType(ScheduleType.SERVICE);
    PseudonymAppointmentModel.stepEight.hasSelectedConsultations = this.filterSuggestionByType(ScheduleType.CONSULTATION);

    setTimeout(() => {
      this.panelLoaderConfirmationApi.setVisible(false);
      this.panelSummaryExamsApi.setVisible(PseudonymAppointmentModel.stepEight.hasSelectedExams);
      this.panelSummaryServicesApi.setVisible(PseudonymAppointmentModel.stepEight.hasSelectedSevices);
      this.panelSummaryConsultationsApi.setVisible(PseudonymAppointmentModel.stepEight.hasSelectedConsultations);
      this.panelSummaryWaitingListApi.setVisible(PseudonymAppointmentModel.stepEight.hasWaitingList);
    }, 3000);
  }

  private getTeleconsultationTypeExpression(item: ScheduleItem, isWaitingListItem: boolean | undefined): string {
    if (isWaitingListItem) {
      return MessageService.translate(TELECONSULTATION_EXPRESSION_TYPE.WATING_LIST);
    }

    const type = item.suggestionTypeConsultation ?? item.teleconsultationType;
    switch (type) {
      case TELECONSULTATION_TYPE.TELECONSULTATION:
        return MessageService.translate(TELECONSULTATION_EXPRESSION_TYPE.TELECONSULTATION);
      case TELECONSULTATION_TYPE.RESQUEST:
        return MessageService.translate(TELECONSULTATION_EXPRESSION_TYPE.RESQUEST);
      case TELECONSULTATION_TYPE.IN_PERSON:
      default:
        return MessageService.translate(TELECONSULTATION_EXPRESSION_TYPE.IN_PERSON);
    }
  }

  private getExpressionType(type: string | undefined): string {
    if (!type) {
      return ''
    }

    switch (type) {
      case ScheduleType.CONSULTATION:
        return MessageService.translate(ScheduleExpressionType.CONSULTATION);
      case ScheduleType.EXAM:
        return MessageService.translate(ScheduleExpressionType.EXAM);
      case ScheduleType.SERVICE:
        return MessageService.translate(ScheduleExpressionType.SERVICE);
      default:
        return '';
    }
  }

  private getProcedureIcon(type: string | undefined): string {
    if (!type) {
      return ''
    }

    switch (type) {
      case ScheduleType.CONSULTATION:
        return AppointmentsIcons.CONSULTATION;
      case ScheduleType.EXAM:
        return AppointmentsIcons.EXAM;
      case ScheduleType.SERVICE:
        return AppointmentsIcons.SERVICE;
      default:
        return '';
    }
  }

  private filterSuggestionByType(type: string): boolean {
    const selectedItemsList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;
    return selectedItemsList.filter(item => item.type == type && !!item.startDate).length > 0;
  }

  private filterSuggestionWaitingList(integratedScheduleItemSequence: string): boolean {
    const suggestionWaitingList = PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;

    return suggestionWaitingList.nrSeqAgeintItem == integratedScheduleItemSequence;
  }

  private additionalInformationWaitingList(arrayScheduleItem: Array<ScheduleItem>): void {
    const pseudonymWaitingListData = PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;
    arrayScheduleItem.forEach(item => {
      if (pseudonymWaitingListData.nrSeqAgeintItem === item.nrSeqAgeintItem) {
        item.startDate = pseudonymWaitingListData.availablePeriod[0];
      }
    })
  }

  private getArrayScheduleItemCopy(arrayScheduleItem: Array<ScheduleItem>): Array<ScheduleItem> {
    const jsonObject = JSON.stringify(arrayScheduleItem);
    return JSON.parse(jsonObject) as Array<ScheduleItem>;
  }
}
